export type PromptLibraryCategoryId =
	| 'personalized'
	| 'give_recognition'
	| 'prep_follow_up'
	| 'catch_up'
	| 'coach_team'
	| 'spend_points'
	| 'admin_program'
	| 'admin_recognition'
	| 'admin_teams'
	| 'admin_rewards'
	| 'admin_setup'
	| 'get_help';

export type PromptConnector = 'granola' | 'linear' | 'github';

export interface PromptLibraryPrompt {
	id: string;
	key: string;
	categoryId: PromptLibraryCategoryId;
	sourceKeys: string[];
	text?: string;
	workflowName?: string;
	score?: number | null;
	connector?: PromptConnector;
	// True when this is a connector prompt the company has enabled but the
	// current user hasn't connected yet. The modal renders these as a dimmed
	// "Connect {connector} to unlock" preview that routes to Bizy connector
	// settings instead of sending the prompt — a soft nudge to connect.
	locked: boolean;
}

export interface PromptLibraryCategory {
	id: PromptLibraryCategoryId;
	labelKey: string;
	descriptionKey: string;
	prompts: PromptLibraryPrompt[];
	adminOnly?: boolean;
}

type PromptLibraryGates = {
	isAdmin: boolean;
	canGiveRecognition?: boolean;
	canAccessOneOnOnes?: boolean;
	canAccessTeamHub?: boolean;
	canAccessGoals?: boolean;
	canAccessAdvice?: boolean;
	canAccessCheckIn?: boolean;
	canAccessIncentives?: boolean;
	canAccessNominations?: boolean;
	canAccessAnnouncements?: boolean;
	canAdministerAnnouncements?: boolean;
	canAccessUserManagement?: boolean;
	canManageUserImport?: boolean;
	canManagePeerToPeer?: boolean;
	canAccessAnalytics?: boolean;
	canViewAdmin?: boolean;
	hasGranolaConnector?: boolean;
	hasLinearConnector?: boolean;
	hasGithubConnector?: boolean;
	isGranolaConnected?: boolean;
	isLinearConnected?: boolean;
	isGithubConnected?: boolean;
};

export interface PersonalizedPromptInput {
	id: string;
	prompt: string;
	workflow_name?: string;
	workflow_rank?: number;
	score?: number | null;
	prompt_key?: string;
}

const favoriteStorageKeyPrefix = 'bizy-prompt-library-favorites';

function favoriteStorageKeyForUser(userId: string): string {
	return `${favoriteStorageKeyPrefix}:${userId}`;
}

const BONUSLY_SOURCE = ['promptLibrary.sources.bonusly'];
const PERSONALIZED_SOURCE = ['promptLibrary.sources.personalized'];
const CONNECTOR_SOURCE_KEYS: Record<PromptConnector, string> = {
	granola: 'promptLibrary.sources.granola',
	linear: 'promptLibrary.sources.linear',
	github: 'promptLibrary.sources.github',
};

// A connector prompt is visible once the company enables the integration and
// the current user's connection status is known. Known disconnected prompts
// render as locked previews; unknown/loading prompts stay out of the sendable
// pool so they can't be submitted before the status query resolves.
const CONNECTOR_COMPANY_GATES: Record<PromptConnector, (options: PromptLibraryGates) => boolean> = {
	granola: ({ hasGranolaConnector }) => hasGranolaConnector === true,
	linear: ({ hasLinearConnector }) => hasLinearConnector === true,
	github: ({ hasGithubConnector }) => hasGithubConnector === true,
};
const CONNECTOR_CONNECTED_GATES: Record<PromptConnector, (options: PromptLibraryGates) => boolean | undefined> = {
	granola: ({ isGranolaConnected }) => isGranolaConnected,
	linear: ({ isLinearConnected }) => isLinearConnected,
	github: ({ isGithubConnected }) => isGithubConnected,
};

interface PromptDefinition {
	id: string;
	key: string;
	connector?: PromptConnector;
	isAvailable?: (options: PromptLibraryGates) => boolean;
	/** Dev-only temporary hide. Re-enable by removing this flag and its comment. */
	excluded?: boolean;
}

interface CategoryDefinition {
	id: PromptLibraryCategoryId;
	labelKey: string;
	descriptionKey: string;
	adminOnly?: boolean;
	isAvailable?: (options: PromptLibraryGates) => boolean;
	prompts: PromptDefinition[];
}

// Library catalog. Each prompt is a curated, hand-written question the user
// can click to send to Bizy. Distinct from page-level `suggestedPrompts.*`
// chips (which adapt per page). Permission gates hide prompts whose target
// surface a user can't access; categories with zero remaining prompts are
// dropped entirely.
const categoryDefinitions: CategoryDefinition[] = [
	{
		id: 'give_recognition',
		labelKey: 'promptLibrary.categories.giveRecognition',
		descriptionKey: 'promptLibrary.categoryDescriptions.giveRecognition',
		isAvailable: ({ canGiveRecognition }) => canGiveRecognition !== false,
		prompts: [
			{ id: 'suggestRecognize', key: 'promptLibrary.library.giveRecognition.suggestRecognize' },
			{ id: 'upcomingCelebration', key: 'promptLibrary.library.giveRecognition.upcomingCelebration' },
			{ id: 'notRecognizedAWhile', key: 'promptLibrary.library.giveRecognition.notRecognizedAWhile' },
			{
				id: 'teamNotRecognized',
				key: 'promptLibrary.library.giveRecognition.teamNotRecognized',
				isAvailable: ({ canAccessTeamHub }) => canAccessTeamHub !== false,
			},
			{
				id: 'teamWorthRecognizing',
				key: 'promptLibrary.library.giveRecognition.teamWorthRecognizing',
				isAvailable: ({ canAccessTeamHub }) => canAccessTeamHub !== false,
			},
			{ id: 'draftForPerson', key: 'promptLibrary.library.giveRecognition.draftForPerson' },
			{
				id: 'draftFromLinear',
				key: 'promptLibrary.library.giveRecognition.draftFromLinear',
				connector: 'linear',
			},
			{
				id: 'recognizeShippedLinear',
				key: 'promptLibrary.library.giveRecognition.recognizeShippedLinear',
				connector: 'linear',
			},
			{
				id: 'draftFromGithub',
				key: 'promptLibrary.library.giveRecognition.draftFromGithub',
				connector: 'github',
			},
			{
				id: 'recognizeReviewer',
				key: 'promptLibrary.library.giveRecognition.recognizeReviewer',
				connector: 'github',
			},
			{
				id: 'recognitionFromMeeting',
				key: 'promptLibrary.library.giveRecognition.recognitionFromMeeting',
				connector: 'granola',
			},
			{
				id: 'recognizeFromConversations',
				key: 'promptLibrary.library.giveRecognition.recognizeFromConversations',
			},
			{ id: 'whatPeopleRecognize', key: 'promptLibrary.library.giveRecognition.whatPeopleRecognize' },
		],
	},
	{
		id: 'catch_up',
		labelKey: 'promptLibrary.categories.catchUp',
		descriptionKey: 'promptLibrary.categoryDescriptions.catchUp',
		prompts: [
			{
				id: 'teamActivity',
				key: 'promptLibrary.library.catchUp.teamActivity',
				isAvailable: ({ canAccessTeamHub }) => canAccessTeamHub !== false,
			},
			{ id: 'myRecentWork', key: 'promptLibrary.library.catchUp.myRecentWork' },
			{
				id: 'teamThisWeek',
				key: 'promptLibrary.library.catchUp.teamThisWeek',
				isAvailable: ({ canAccessTeamHub }) => canAccessTeamHub !== false,
			},
			{
				id: 'recentAdvice',
				key: 'promptLibrary.library.catchUp.recentAdvice',
				isAvailable: ({ canAccessAdvice }) => canAccessAdvice !== false,
			},
			// Temporarily excluded (eval score 60): recognition-connection analytics returned empty and
			// meeting history was unavailable to Bizy. Re-enable once closest-collaborator queries use
			// connection + meeting data reliably.
			{ id: 'closestCollaborators', key: 'promptLibrary.library.catchUp.closestCollaborators', excluded: true },
			{
				id: 'granolaThemes',
				key: 'promptLibrary.library.catchUp.granolaThemes',
				connector: 'granola',
			},
			{
				id: 'linearPlate',
				key: 'promptLibrary.library.catchUp.linearPlate',
				connector: 'linear',
			},
			{
				id: 'githubAwaitingReview',
				key: 'promptLibrary.library.catchUp.githubAwaitingReview',
				connector: 'github',
			},
		],
	},
	{
		id: 'coach_team',
		labelKey: 'promptLibrary.categories.coachTeam',
		descriptionKey: 'promptLibrary.categoryDescriptions.coachTeam',
		isAvailable: ({ canAccessTeamHub, canAccessGoals, canAccessCheckIn }) =>
			[canAccessTeamHub, canAccessGoals, canAccessCheckIn].some((permission) => permission !== false),
		prompts: [
			{
				id: 'whoNeedsAttention',
				key: 'promptLibrary.library.coachTeam.whoNeedsAttention',
				isAvailable: ({ canAccessTeamHub }) => canAccessTeamHub !== false,
			},
			{ id: 'howIsPerson', key: 'promptLibrary.library.coachTeam.howIsPerson' },
			{
				id: 'goalsSlipping',
				key: 'promptLibrary.library.coachTeam.goalsSlipping',
				isAvailable: ({ canAccessGoals }) => canAccessGoals !== false,
			},
			{
				id: 'checkinParticipation',
				key: 'promptLibrary.library.coachTeam.checkinParticipation',
				isAvailable: ({ canAccessCheckIn }) => canAccessCheckIn !== false,
			},
		],
	},
	{
		id: 'spend_points',
		labelKey: 'promptLibrary.categories.spendPoints',
		descriptionKey: 'promptLibrary.categoryDescriptions.spendPoints',
		prompts: [
			{ id: 'pointsBalance', key: 'promptLibrary.library.spendPoints.pointsBalance' },
			{ id: 'redeem', key: 'promptLibrary.library.spendPoints.redeem' },
			{ id: 'availableRewards', key: 'promptLibrary.library.spendPoints.availableRewards' },
			{ id: 'redemptionHistory', key: 'promptLibrary.library.spendPoints.redemptionHistory' },
			{
				id: 'incentivesWork',
				key: 'promptLibrary.library.spendPoints.incentivesWork',
				isAvailable: ({ canAccessIncentives }) => canAccessIncentives !== false,
			},
		],
	},
	{
		id: 'prep_follow_up',
		labelKey: 'promptLibrary.categories.prepFollowUp',
		descriptionKey: 'promptLibrary.categoryDescriptions.prepFollowUp',
		isAvailable: ({ canAccessOneOnOnes }) => canAccessOneOnOnes !== false,
		prompts: [
			{ id: 'needsAgenda', key: 'promptLibrary.library.prepFollowUp.needsAgenda' },
			{ id: 'prepForThis', key: 'promptLibrary.library.prepFollowUp.prepForThis' },
			{ id: 'openActionItems', key: 'promptLibrary.library.prepFollowUp.openActionItems' },
			{ id: 'lastAgreement', key: 'promptLibrary.library.prepFollowUp.lastAgreement' },
			{ id: 'catchUp', key: 'promptLibrary.library.prepFollowUp.catchUp' },
			{
				id: 'granolaPrep',
				key: 'promptLibrary.library.prepFollowUp.granolaPrep',
				connector: 'granola',
			},
			{
				id: 'granolaActionItems',
				key: 'promptLibrary.library.prepFollowUp.granolaActionItems',
				connector: 'granola',
			},
			{
				id: 'linearPrep',
				key: 'promptLibrary.library.prepFollowUp.linearPrep',
				connector: 'linear',
			},
			{
				id: 'githubPrep',
				key: 'promptLibrary.library.prepFollowUp.githubPrep',
				connector: 'github',
			},
		],
	},
	{
		id: 'admin_program',
		labelKey: 'promptLibrary.categories.adminProgram',
		descriptionKey: 'promptLibrary.categoryDescriptions.adminProgram',
		adminOnly: true,
		isAvailable: ({ canViewAdmin, canAccessAnalytics }) => canViewAdmin !== false && canAccessAnalytics !== false,
		prompts: [
			{ id: 'programHealth', key: 'promptLibrary.library.admin.programHealth' },
			{ id: 'participationTrend', key: 'promptLibrary.library.admin.participationTrend' },
			{ id: 'bonuslyRoi', key: 'promptLibrary.library.admin.bonuslyRoi' },
		],
	},
	{
		id: 'admin_recognition',
		labelKey: 'promptLibrary.categories.adminRecognition',
		descriptionKey: 'promptLibrary.categoryDescriptions.adminRecognition',
		adminOnly: true,
		isAvailable: ({ canViewAdmin, canAccessAnalytics }) => canViewAdmin !== false && canAccessAnalytics !== false,
		prompts: [
			{ id: 'givingReceiving', key: 'promptLibrary.library.admin.givingReceiving' },
			{ id: 'topCollaborators', key: 'promptLibrary.library.admin.topCollaborators' },
			{ id: 'recognitionQuality', key: 'promptLibrary.library.admin.recognitionQuality' },
			{
				id: 'pulseResults',
				key: 'promptLibrary.library.admin.pulseResults',
				isAvailable: ({ canAccessCheckIn }) => canAccessCheckIn !== false,
			},
		],
	},
	{
		id: 'admin_teams',
		labelKey: 'promptLibrary.categories.adminTeams',
		descriptionKey: 'promptLibrary.categoryDescriptions.adminTeams',
		adminOnly: true,
		isAvailable: ({ canViewAdmin, canAccessAnalytics }) => canViewAdmin !== false && canAccessAnalytics !== false,
		prompts: [
			{ id: 'teamsGoneQuiet', key: 'promptLibrary.library.admin.teamsGoneQuiet' },
			{ id: 'unrecognizedDepartments', key: 'promptLibrary.library.admin.unrecognizedDepartments' },
			{ id: 'teamConnections', key: 'promptLibrary.library.admin.teamConnections' },
			// Temporarily excluded (eval score 70): team-level pulse rows are not returned from analytics
			// views the Data Agent can query. Re-enable after team-level pulse data and tool support
			// are wired end-to-end.
			{ id: 'highestPulseTeamsAndCadence', key: 'promptLibrary.library.admin.highestPulseTeamsAndCadence', excluded: true },
			// Temporarily excluded (eval score 70): Bizy routed to participation reports instead of 1:1
			// meeting cadence. Re-enable once admin manager 1:1 cadence reporting works end-to-end.
			{ id: 'managerOneOnOneCadence', key: 'promptLibrary.library.admin.managerOneOnOneCadence', excluded: true },
		],
	},
	{
		id: 'admin_rewards',
		labelKey: 'promptLibrary.categories.adminRewards',
		descriptionKey: 'promptLibrary.categoryDescriptions.adminRewards',
		adminOnly: true,
		isAvailable: ({ canViewAdmin, canAccessAnalytics }) => canViewAdmin !== false && canAccessAnalytics !== false,
		prompts: [
			{ id: 'pointsUsage', key: 'promptLibrary.library.admin.pointsUsage' },
			{ id: 'popularRewards', key: 'promptLibrary.library.admin.popularRewards' },
			{ id: 'redemptionPatterns', key: 'promptLibrary.library.admin.redemptionPatterns' },
		],
	},
	{
		id: 'admin_setup',
		labelKey: 'promptLibrary.categories.adminSetup',
		descriptionKey: 'promptLibrary.categoryDescriptions.adminSetup',
		adminOnly: true,
		isAvailable: ({ canViewAdmin }) => canViewAdmin !== false,
		prompts: [
			{ id: 'inviteTeam', key: 'promptLibrary.library.admin.inviteTeam' },
			{
				id: 'createUser',
				key: 'promptLibrary.library.admin.createUser',
				isAvailable: ({ canAccessUserManagement }) => canAccessUserManagement !== false,
			},
			{
				id: 'lookUpUser',
				key: 'promptLibrary.library.admin.lookUpUser',
				isAvailable: ({ canAccessUserManagement }) => canAccessUserManagement !== false,
			},
			{
				id: 'updateUserManager',
				key: 'promptLibrary.library.admin.updateUserManager',
				isAvailable: ({ canAccessUserManagement }) => canAccessUserManagement !== false,
			},
			{
				id: 'deactivateUser',
				key: 'promptLibrary.library.admin.deactivateUser',
				isAvailable: ({ canAccessUserManagement }) => canAccessUserManagement !== false,
			},
			{
				id: 'deactivateUsersBatch',
				key: 'promptLibrary.library.admin.deactivateUsersBatch',
				isAvailable: ({ canAccessUserManagement }) => canAccessUserManagement !== false,
			},
			{
				id: 'reactivateUser',
				key: 'promptLibrary.library.admin.reactivateUser',
				isAvailable: ({ canAccessUserManagement }) => canAccessUserManagement !== false,
			},
			{
				id: 'updateCompanyValues',
				key: 'promptLibrary.library.admin.updateCompanyValues',
				isAvailable: ({ canManagePeerToPeer }) => canManagePeerToPeer !== false,
			},
			{ id: 'connectSlackTeams', key: 'promptLibrary.library.admin.connectSlackTeams' },
			{
				id: 'hrisIntegration',
				key: 'promptLibrary.library.admin.hrisIntegration',
				isAvailable: ({ canManageUserImport }) => canManageUserImport !== false,
			},
			{
				id: 'createAnnouncement',
				key: 'promptLibrary.library.admin.createAnnouncement',
				isAvailable: ({ canAccessAnnouncements, canAdministerAnnouncements }) => canAccessAnnouncements !== false && canAdministerAnnouncements !== false,
			},
			{
				id: 'launchIncentive',
				key: 'promptLibrary.library.admin.launchIncentive',
				isAvailable: ({ canAccessIncentives }) => canAccessIncentives !== false,
			},
			{
				id: 'setupNominations',
				key: 'promptLibrary.library.admin.setupNominations',
				isAvailable: ({ canAccessNominations }) => canAccessNominations !== false,
			},
		],
	},
	{
		id: 'get_help',
		labelKey: 'promptLibrary.categories.getHelp',
		descriptionKey: 'promptLibrary.categoryDescriptions.getHelp',
		prompts: [
			{ id: 'featureRequest', key: 'promptLibrary.library.getHelp.featureRequest' },
			{ id: 'somethingNotWorking', key: 'promptLibrary.library.getHelp.somethingNotWorking' },
		],
	},
];

function buildPersonalizedCategory(prompts: PersonalizedPromptInput[] = []): PromptLibraryCategory | null {
	const personalizedPrompts = prompts
		.filter((prompt) => prompt.prompt?.trim())
		.sort((a, b) => (a.workflow_rank ?? Number.MAX_SAFE_INTEGER) - (b.workflow_rank ?? Number.MAX_SAFE_INTEGER))
		.map<PromptLibraryPrompt>((prompt) => ({
			id: `personalized:${prompt.id}`,
			key: prompt.prompt_key ?? `personalized:${prompt.id}`,
			categoryId: 'personalized',
			sourceKeys: PERSONALIZED_SOURCE,
			text: prompt.prompt,
			workflowName: prompt.workflow_name,
			score: prompt.score,
			locked: false,
		}));

	if (personalizedPrompts.length === 0) return null;

	return {
		id: 'personalized',
		labelKey: 'promptLibrary.categories.personalized',
		descriptionKey: 'promptLibrary.categoryDescriptions.personalized',
		prompts: personalizedPrompts,
	};
}

function findPromptDefinition(promptKey: string): PromptDefinition | undefined {
	for (const category of categoryDefinitions) {
		const match = category.prompts.find((prompt) => prompt.key === promptKey);
		if (match) return match;
	}

	return undefined;
}

export function isPromptLibraryPromptExcluded(promptKey: string): boolean {
	return findPromptDefinition(promptKey)?.excluded === true;
}

function isPromptIncluded(definition: PromptDefinition, options: PromptLibraryGates): boolean {
	if (definition.excluded) {
		if (import.meta.env.DEV) {
			console.info(`[Bizy prompt library] Excluding ${definition.key} (see catalog comment in prompt-library.ts)`);
		}
		return false;
	}

	// Connector prompts require the company to have enabled the integration —
	// otherwise the user has nowhere to connect and a locked preview would be a
	// dead end. Once enabled, the user's connection status must resolve before
	// the prompt appears; unknown status could otherwise submit a locked prompt.
	if (definition.connector) {
		if (!CONNECTOR_COMPANY_GATES[definition.connector](options)) return false;
		if (CONNECTOR_CONNECTED_GATES[definition.connector](options) === undefined) return false;
	}
	if (definition.isAvailable && !definition.isAvailable(options)) return false;
	return true;
}

function buildPrompt(categoryId: PromptLibraryCategoryId, definition: PromptDefinition, options: PromptLibraryGates): PromptLibraryPrompt {
	const connectorSourceKey = definition.connector ? CONNECTOR_SOURCE_KEYS[definition.connector] : undefined;
	const locked = definition.connector ? CONNECTOR_CONNECTED_GATES[definition.connector](options) === false : false;

	return {
		id: `${categoryId}:${definition.id}`,
		key: definition.key,
		categoryId,
		sourceKeys: connectorSourceKey ? [...BONUSLY_SOURCE, connectorSourceKey] : BONUSLY_SOURCE,
		connector: definition.connector,
		locked,
	};
}

export function promptLibraryPromptText(
	prompt: PromptLibraryPrompt,
	translate: (key: string, options?: Record<string, unknown>) => string,
	promptName?: string
): string {
	return prompt.text ?? translate(prompt.key, { name: promptName });
}

export function getPromptLibraryCategories(options: PromptLibraryGates, personalizedPrompts: PersonalizedPromptInput[] = []): PromptLibraryCategory[] {
	const personalizedCategory = buildPersonalizedCategory(personalizedPrompts);
	const categories = categoryDefinitions
		.filter((category) => !category.adminOnly || options.isAdmin)
		.filter((category) => !category.isAvailable || category.isAvailable(options))
		.map<PromptLibraryCategory>((category) => ({
			id: category.id,
			labelKey: category.labelKey,
			descriptionKey: category.descriptionKey,
			adminOnly: category.adminOnly,
			prompts: category.prompts
				.filter((prompt) => isPromptIncluded(prompt, options))
				.map((prompt) => buildPrompt(category.id, prompt, options))
				// Surface ready-to-use prompts first; sink locked connector
				// previews to the end of the section as "unlock these" nudges.
				.sort((a, b) => Number(a.locked) - Number(b.locked)),
		}))
		.filter((category) => category.prompts.length > 0);

	return personalizedCategory ? [personalizedCategory, ...categories] : categories;
}

export function getPromptLibraryPromptByKey(
	key: string,
	options: PromptLibraryGates,
	personalizedPrompts: PersonalizedPromptInput[] = []
): PromptLibraryPrompt | undefined {
	return getPromptLibraryCategories(options, personalizedPrompts)
		.flatMap((category) => category.prompts)
		.find((prompt) => prompt.key === key);
}

export function loadFavoritePromptKeys(userId: string): string[] {
	if (!userId || typeof window === 'undefined') return [];
	try {
		const raw = window.localStorage.getItem(favoriteStorageKeyForUser(userId));
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
	} catch {
		return [];
	}
}

export function saveFavoritePromptKeys(userId: string, keys: string[]): void {
	if (!userId || typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(favoriteStorageKeyForUser(userId), JSON.stringify(Array.from(new Set(keys))));
	} catch {
		// localStorage unavailable
	}
}

export function toggleFavoritePromptKey(keys: string[], key: string): string[] {
	return keys.includes(key) ? keys.filter((favorite) => favorite !== key) : [...keys, key];
}

export function getFavoritePromptLibraryPrompts(
	keys: string[],
	options: PromptLibraryGates,
	personalizedPrompts: PersonalizedPromptInput[] = []
): PromptLibraryPrompt[] {
	return (
		keys
			.map((key) => getPromptLibraryPromptByKey(key, options, personalizedPrompts))
			// Drop locked previews: a connector prompt the user can't send yet
			// shouldn't appear as a one-click favorite in the menu.
			.filter((prompt): prompt is PromptLibraryPrompt => prompt != null && !prompt.locked)
	);
}
